import { useState, type ReactNode } from "react"
import { X, UserPlus2, ShieldCheck, Phone, UserRound, BriefcaseBusiness } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { createUser } from "../../api/products/post"
import { toast } from "react-hot-toast"

type Props = {
  currentStore: any
  isOpen: boolean
  onClose: () => void
  onAdd: (newUser: any) => void
}

export default function AddUserModal({
  currentStore,
  isOpen,
  onClose,
  onAdd,
}: Props) {
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [countryCode, setCountryCode] = useState("+225")
  const [role, setRole] = useState("STAFF")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const COUNTRY_CODES = [
    { code: "+225", flag: "🇨🇮" },
    { code: "+221", flag: "🇸🇳" },
    { code: "+226", flag: "🇧🇫" },
    { code: "+229", flag: "🇧🇯" },
    { code: "+228", flag: "🇹🇬" },
    { code: "+237", flag: "🇨🇲" },
  ]

  const resetForm = () => {
    setName("")
    setPhone("")
    setCountryCode("+225")
    setRole("STAFF")
    setError("")
  }

  const handleClose = () => {
    if (loading) return
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      setError("Tous les champs sont obligatoires")
      return
    }

    try {
      setLoading(true)
      setError("")

      const res = await createUser(currentStore._id, {
        name: name.trim(),
        phone: `${countryCode}${phone.trim()}`,
        countryCode,
        role,
      })

      onAdd(res?.user || res)
      if (res?.inviteSent && res?.inviteLink) {
        toast.success("Invitation envoyée au collaborateur")
      } else {
        toast.success("Utilisateur créé")
      }
      resetForm()
      onClose()
    } catch (e: any) {
      setError(e?.message || "Erreur lors de la création")
    } finally {
      setLoading(false)
    }
  }

  const isAdmin = role === "ADMIN"

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            initial={{ scale: 0.94, y: 36 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.96, y: 24 }}
            transition={{ duration: 0.25 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="relative overflow-hidden bg-gradient-to-br from-cyan-600 via-sky-700 to-blue-900 p-6 text-white sm:p-7">
                <div className="pointer-events-none absolute -top-12 left-6 h-28 w-28 rounded-full bg-white/10 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-10 right-0 h-36 w-36 rounded-full bg-cyan-300/20 blur-3xl" />

                <div className="relative">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                    <UserPlus2 size={22} />
                  </div>
                  <h2 className="mt-5 text-2xl font-bold">Ajouter un membre</h2>
                  <p className="mt-2 text-sm text-white/85">
                    Créez rapidement un compte pour un vendeur ou un administrateur et donnez-lui
                    l’accès adapté à son rôle.
                  </p>

                  <div className="mt-8 space-y-3">
                    <Feature
                      icon={<ShieldCheck size={16} />}
                      title="Rôles clairs"
                      description="Attribuez un accès administrateur ou vendeur selon le besoin."
                    />
                    <Feature
                      icon={<Phone size={16} />}
                      title="Contact direct"
                      description="Le numéro saisi servira d’identifiant de contact opérationnel."
                    />
                    <Feature
                      icon={<BriefcaseBusiness size={16} />}
                      title="Gestion simple"
                      description="Le compte pourra ensuite être activé ou désactivé depuis la liste."
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Nouveau compte utilisateur</p>
                    <p className="mt-1 text-xs text-slate-500">
                      Boutique: {currentStore?.name || "Boutique active"}
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 disabled:opacity-50"
                  >
                    <X size={18} />
                  </button>
                </div>

                {error && (
                  <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {error}
                  </div>
                )}

                <div className="mt-6 space-y-5">
                  <Field label="Nom complet" hint="Nom visible dans la gestion d’équipe">
                    <div className="relative">
                      <UserRound size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        placeholder="Ex: Awa Kouassi"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={loading}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                      />
                    </div>
                  </Field>

                  <Field label="Téléphone" hint="Numéro joignable pour ce compte">
                    <div className="flex gap-2">
                      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3">
                        <span className="text-lg leading-none">
                          {(COUNTRY_CODES.find((item) => item.code === countryCode) || COUNTRY_CODES[0]).flag}
                        </span>
                        <select
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          disabled={loading}
                          className="border-none bg-transparent text-sm font-semibold text-slate-700 outline-none focus:ring-0"
                        >
                          {COUNTRY_CODES.map((item) => (
                            <option key={item.code} value={item.code}>
                              {item.code}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="relative flex-1">
                        <Phone size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        placeholder="Ex: 0700000000"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        disabled={loading}
                        className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100 disabled:opacity-60"
                      />
                      </div>
                    </div>
                  </Field>

                  <Field label="Rôle" hint="Choisissez le niveau d’accès à accorder">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <RoleOption
                        title="Vendeur"
                        description="Gère les opérations courantes et les ventes."
                        active={!isAdmin}
                        onClick={() => setRole("STAFF")}
                      />
                      <RoleOption
                        title="Administrateur"
                        description="Dispose d’un accès avancé sur la boutique."
                        active={isAdmin}
                        onClick={() => setRole("ADMIN")}
                      />
                    </div>
                  </Field>
                </div>

                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    onClick={handleClose}
                    disabled={loading}
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    disabled={loading}
                    onClick={handleSubmit}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                  >
                    {loading && (
                      <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    )}
                    {loading ? "Création en cours..." : "Créer l’utilisateur"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint: string
  children: ReactNode
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label className="text-sm font-semibold text-slate-800">{label}</label>
        <span className="text-xs text-slate-500">{hint}</span>
      </div>
      {children}
    </div>
  )
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-xl bg-white/15">
          {icon}
        </div>
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="mt-1 text-xs leading-5 text-white/80">{description}</p>
        </div>
      </div>
    </div>
  )
}

function RoleOption({
  title,
  description,
  active,
  onClick,
}: {
  title: string
  description: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border px-4 py-4 text-left transition ${
        active
          ? "border-blue-300 bg-blue-50 ring-4 ring-blue-100"
          : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white"
      }`}
    >
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
    </button>
  )
}
